/* eslint-disable spire/fenced-imports */
/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Html5Qrcode } from "html5-qrcode";
import React, { useEffect, useRef } from "react";

const elementID = "barcode-scanner-live-stream";

interface OwnProps {
    onChange: (text: string, isUrl?: boolean) => void;
    onError: (errorMessage: any) => void;
    isOpen?: boolean;
    androidAspectRatio?: string;
    desktopAspectRatio?: string;
    iPhoneAspectRatio?: string;
}

type Props = OwnProps;

const ScannerModalContent = ({
    onChange,
    onError,
    isOpen,
    androidAspectRatio,
    desktopAspectRatio,
    iPhoneAspectRatio,
}: Props) => {
    const urlRegex = new RegExp(
        /(?:(?:https?|ftp):\/\/|\b(?:[a-z\d]+\.))(?:(?:[^\s()<>]+|\((?:[^\s()<>]+|(?:\([^\s()<>]+\)))?\))+(?:\((?:[^\s()<>]+|(?:\(?:[^\s()<>]+\)))?\)|[^\s`!()\[\]{};:'".,<>?«»""'']))?/gi,
    );
    const checkIfUrl = (text: string) => !!(text && urlRegex.test(text));

    const qrboxFunction = (viewfinderWidth: number, viewfinderHeight: number) => {
        const minEdgePercentage = 1;
        const minEdgeSize = Math.max(50, Math.min(viewfinderWidth, viewfinderHeight));
        const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
        return {
            width: qrboxSize,
            height: qrboxSize,
        };
    };

    const scanHistory = useRef<{ text: string; time: number }[]>([]);
    const requiredMatches = 3;
    const requiredTime = 1000; // 1 second in milliseconds

    const isIphone = navigator.userAgent.includes("iphone");
    const isDesktop = !/Mobi|Android|iPad|iPhone|Touch|Tablet/i.test(navigator.userAgent);

    // Set aspect ratio based on device type to get the widest screen as much as possible
    // Desktop 16 / 9
    // iPhone 9 / 16
    // Android 9 / 16

    const getAspectRatio = () => {
        if (isDesktop) {
            return desktopAspectRatio ? parseFloat(desktopAspectRatio) : 16 / 9;
        }
        if (isIphone) {
            return iPhoneAspectRatio ? parseFloat(iPhoneAspectRatio) : 9 / 16;
        }
        return androidAspectRatio ? parseFloat(androidAspectRatio) : 9 / 16; // Default for Android
    };

    useEffect(() => {
        const qrCodeSuccessCallback = (decodedText: string, decodedResult: any) => {
            const now = Date.now();
            scanHistory.current.push({ text: decodedText, time: now });

            // Filter to keep scans within the required time window
            scanHistory.current = scanHistory.current.filter(scan => now - scan.time <= requiredTime);

            // Check if the barcode matches enough times within the required time
            const matches = scanHistory.current.filter(scan => scan.text === decodedText).length;

            if (matches >= requiredMatches && html5QrCode.isScanning) {
                html5QrCode
                    .stop()
                    .then(ignore => {
                        onChange(decodedText, checkIfUrl(decodedText));
                    })
                    .catch(errorMessage => {
                        onError(errorMessage);
                    });
            }
        };

        const qrCodeErrorCallback = (errorMessage: string) => {
            if (errorMessage.indexOf("No MultiFormat Readers were able to detect the code.") < 0) {
                onError(errorMessage);
            }
        };

        const html5QrCode = new Html5Qrcode(elementID);

        const scanQRCode = async () => {
            try {
                const element = document.getElementById(elementID);
                if (element) {
                    await html5QrCode.start(
                        { facingMode: "environment" },
                        {
                            fps: 5,
                            aspectRatio: getAspectRatio(),
                        },
                        qrCodeSuccessCallback,
                        qrCodeErrorCallback,
                    );
                } else {
                    onError("Element not found");
                }
            } catch (errorMessage) {
                onError(errorMessage);
            }
        };

        // Ensure the canvas is ready before starting the QR code scanning
        setTimeout(() => {
            scanQRCode();
        }, 1000); // Adjust the delay as needed

        // PAC-1307
        // release camera after closing the barcode reader
        // had to setTimeout with 300 to make sure the barcode reader is still close
        return () => {
            setTimeout(() => {
                if (html5QrCode.isScanning) {
                    html5QrCode
                        .stop()
                        .then(() => {
                            html5QrCode?.clear();
                        })
                        .catch(error => {
                            console.log(error);
                        });
                }
            }, 3000);
        };
    }, []);

    return (
        <StyledWrapper
            id={elementID}
            style={{
                maxHeight: "250px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        ></StyledWrapper>
    );
};

export default ScannerModalContent;
